import React, { type ReactNode } from 'react';
import { Checkbox } from '@/components/Checkbox';
import { CheckCircle } from '@/components/Icons/CheckCircle';
import { Forward } from '@/components/Icons/Forward';
import { Paperclip } from '@/components/Icons/Paperclip';
import { Reply } from '@/components/Icons/Reply';
import { Star } from '@/components/Icons/Star';
import { StarFilled } from '@/components/Icons/StarFilled';
import { recipientStatusIcon } from '@/pages/Mail/mailRecipients';
import { FollowUpStatus, MessageStatus } from '@/types/components';
import type { MailMessage } from '@/types/mail';
import { tv } from '@/utils/tv';

const followUpIcon: Record<FollowUpStatus, ReactNode> = {
  [FollowUpStatus.None]: <Star className="size-4 text-muted-foreground" />,
  [FollowUpStatus.Marked]: <StarFilled className="size-4 text-accent" />,
  [FollowUpStatus.Completed]: (
    <CheckCircle className="size-4 text-successful" />
  ),
};

const statusIcon: Record<MessageStatus, ReactNode> = {
  [MessageStatus.Unread]: (
    <span className="size-2 shrink-0 rounded-full bg-accent" />
  ),
  [MessageStatus.Read]: null,
  [MessageStatus.Replied]: (
    <Reply className="size-4 shrink-0 text-subtle-foreground" />
  ),
  [MessageStatus.Forwarded]: (
    <Forward className="size-4 shrink-0 text-subtle-foreground" />
  ),
};

const cell = tv({
  base: 'flex cursor-pointer items-center gap-3 border-b border-divider',
  variants: {
    isUnread: { true: 'bg-card', false: 'bg-background' },
    isMobile: { true: 'px-4 py-3', false: 'px-5 py-4' },
  },
});

const attachment = tv({
  base: 'size-4 shrink-0',
  variants: {
    isUnread: {
      true: 'text-muted-foreground',
      false: 'text-subtle-foreground',
    },
  },
});

const subject = tv({
  base: 'truncate text-foreground',
  variants: {
    isUnread: { true: 'text-subhead-14', false: 'text-body-14' },
  },
});

const desktopName = tv({
  base: 'line-clamp-2 text-foreground',
  variants: {
    isUnread: { true: 'text-subhead-14', false: 'text-body-14' },
  },
});

const desktopPreview = tv({
  base: 'text-body-14 truncate',
  variants: {
    isUnread: {
      true: 'text-muted-foreground',
      false: 'text-subtle-foreground',
    },
  },
});

const desktopDate = tv({
  base: 'w-24 shrink-0 text-right',
  variants: {
    isUnread: {
      true: 'text-subhead-14 text-foreground',
      false: 'text-body-14 text-subtle-foreground',
    },
  },
});

const mobileName = tv({
  base: 'min-w-0 flex-1 truncate text-foreground',
  variants: {
    isUnread: { true: 'text-callout-16', false: 'text-body-16' },
  },
});

const mobilePreview = tv({
  base: 'text-body-14 truncate',
  variants: {
    isUnread: { true: 'text-foreground', false: 'text-subtle-foreground' },
  },
});

const mobileDate = tv({
  base: 'shrink-0',
  variants: {
    isUnread: {
      true: 'text-caption-13 text-foreground',
      false: 'text-body-13 text-muted-foreground',
    },
  },
});

interface Props {
  mail: MailMessage;
  displayName: string;
  showFollowUp: boolean;
  showRecipientStatus: boolean;
  isSelecting: boolean;
  isSelected: boolean;
  isMobile: boolean;
  onCycleFollowUp: (id: string) => void;
  onToggleSelect: (id: string) => void;
  onOpen: (id: string) => void;
}

export const MailCell: React.FC<Props> = ({
  mail,
  displayName,
  showFollowUp,
  showRecipientStatus,
  isSelecting,
  isSelected,
  isMobile,
  onCycleFollowUp,
  onToggleSelect,
  onOpen,
}) => {
  const { hasAttachment, recipientStatus } = mail;
  const isUnread = mail.status === MessageStatus.Unread;
  const readStatusIcon =
    showRecipientStatus && recipientStatus
      ? recipientStatusIcon[recipientStatus]
      : null;

  const handleClick = () => {
    if (isSelecting) {
      onToggleSelect(mail.id);
      return;
    }

    onOpen(mail.id);
  };

  const followUpButton = (
    <button
      type="button"
      aria-label={mail.subject}
      onClick={(event) => {
        event.stopPropagation();
        onCycleFollowUp(mail.id);
      }}
      className="-m-0.5 flex shrink-0 cursor-pointer items-center justify-center p-0.5"
    >
      {followUpIcon[mail.followUpStatus]}
    </button>
  );

  if (isMobile) {
    return (
      <div onClick={handleClick} className={cell({ isUnread, isMobile: true })}>
        {isSelecting && <Checkbox checked={isSelected} onChange={() => {}} />}
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex items-center gap-2">
            {statusIcon[mail.status]}
            <p className={mobileName({ isUnread })}>{displayName}</p>
            {hasAttachment && (
              <Paperclip className={attachment({ isUnread })} />
            )}
            {readStatusIcon}
            <p className={mobileDate({ isUnread })}>{mail.date}</p>
          </div>
          <div className="flex items-center gap-3">
            {mail.subject && (
              <p className={`${subject({ isUnread })} min-w-0 flex-1`}>
                {mail.subject}
              </p>
            )}
            {showFollowUp && followUpButton}
          </div>
          {mail.preview && (
            <p className={mobilePreview({ isUnread })}>{mail.preview}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div onClick={handleClick} className={cell({ isUnread, isMobile: false })}>
      {isSelecting && <Checkbox checked={isSelected} onChange={() => {}} />}
      <div className="flex min-w-0 flex-[2] items-center gap-2 pr-3">
        <span className="flex size-4 shrink-0 items-center justify-center">
          {statusIcon[mail.status]}
        </span>
        <p className={desktopName({ isUnread })}>{displayName}</p>
      </div>

      <div className="flex min-w-0 flex-[4] flex-col gap-1">
        {mail.subject && (
          <p className={subject({ isUnread })}>{mail.subject}</p>
        )}
        {mail.preview && (
          <p className={desktopPreview({ isUnread })}>{mail.preview}</p>
        )}
      </div>

      <div className="flex w-42 shrink-0 items-center justify-end gap-2">
        <span className="flex size-4 shrink-0 items-center justify-center">
          {hasAttachment && <Paperclip className={attachment({ isUnread })} />}
        </span>
        {showFollowUp && followUpButton}
        {showRecipientStatus && (
          <span className="flex size-4 shrink-0 items-center justify-center">
            {readStatusIcon}
          </span>
        )}
        <p className={desktopDate({ isUnread })}>{mail.date}</p>
      </div>
    </div>
  );
};
